import { memo, useMemo } from 'react';
import { Line } from '@react-three/drei';
import { useBIMStore } from '../state/useBIMStore';
import { useAppStore } from '../state/store';
import { useDocumentStore } from '../state/useDocumentStore';
import { resolveVisibility, currentVisibilityInput } from './visibility';
import { dispatchEntityClick, useEntityRegistryRef } from './toolEventBus';
import { extractCrossLines } from './BIMOverlayLayer';

/**
 * BIM Model Reference Layer (2D model space, B4 cross-visibility)
 *
 * The 2D workbench's model space used to draw BIM cross-reference
 * wireframes; when the hybrid drawing views arrived the overlay became the
 * read-only BimDrawingUnderlay and plain model space lost the BIM content
 * entirely. This layer restores it as a LIVE reference: every model
 * element's cross-line wireframe renders at its true world position (A5
 * shared frame), claims its entity-registry slot and routes clicks through
 * dispatchEntityClick — so a wall drawn in the BIM workbench is selectable,
 * erasable and movable (transforms route through the edit router) from the
 * 2D workbench too, under the ledger's visibility/lock language.
 * Representation stays wireframe: shaded fidelity belongs to the 3D/BIM
 * workbenches, and the drawing-view underlays keep their read-only
 * reference semantics.
 */
const REF_COLOR = '#6a7a8a';
const REF_COLOR_SELECTED = '#00aaff';
/** Rendered by their own dedicated renderers in the BIM workbench. */
const SKIP_CATEGORIES = new Set(['Levels', 'Grids']);

export const BimModelReferenceLayer = memo(function BimModelReferenceLayer() {
    const elements = useBIMStore((s) => s.project.elements);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const ledger = useDocumentStore((s) => s.sceneObjects);

    const visibleIds = useMemo(() => {
        const input = currentVisibilityInput();
        const ids: string[] = [];
        for (const [id, element] of elements) {
            if (SKIP_CATEGORIES.has(element.category)) continue;
            const sceneObject = ledger[id];
            if (sceneObject) {
                const r = resolveVisibility(sceneObject, input);
                if (!r.visible || r.locked) continue;
            }
            ids.push(id);
        }
        return ids;
    }, [elements, ledger]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    return (
        <group>
            {visibleIds.map((id) => (
                <BimReferenceItem key={id} elementId={id} isSelected={selectedSet.has(id)} />
            ))}
        </group>
    );
});

const BimReferenceItem = memo(function BimReferenceItem({ elementId, isSelected }: {
    elementId: string;
    isSelected: boolean;
}) {
    const element = useBIMStore((s) => s.project.elements.get(elementId));
    const registryRef = useEntityRegistryRef(elementId, 'bim');
    const polylines = useMemo(() => (element ? extractCrossLines(element) : []), [element]);
    if (polylines.length === 0) return null;
    const color = isSelected ? REF_COLOR_SELECTED : REF_COLOR;
    return (
        <group
            ref={registryRef}
            onClick={(e) => { e.stopPropagation(); dispatchEntityClick(elementId, e as never); }}
        >
            {polylines.map((pts, i) => (
                <Line
                    key={i}
                    points={pts}
                    color={color}
                    lineWidth={isSelected ? 2 : 1}
                    transparent
                    opacity={isSelected ? 0.95 : 0.75}
                />
            ))}
        </group>
    );
});
